import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import ExperienceSelect from './ExperienceSelect'

const TIME_STEPS = (() => {
  const out = []
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += 15) {
      out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
    }
  }
  return out
})()

/** Same rules as the legacy native <select>: from >= minFromTime; to > fromTime. */
const isFromTimeDisabled = (timeString, minFromTime) =>
  !!(minFromTime && timeString < minFromTime)

const isToTimeDisabled = (timeString, fromTime) => {
  if (fromTime === '23:45') {
    return timeString !== '23:45'
  }
  // Older behaviour: when From is empty, every To option stays available.
  return !!(fromTime && timeString <= fromTime)
}

const formatTimeLabel = (timeString) => {
  if (!timeString) return ''
  const [hourString, minute] = timeString.split(':')
  const hour = Number(hourString)
  const period = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour % 12 || 12
  const paddedHour = displayHour.toString().padStart(2, '0')
  return `${paddedHour}:${minute} ${period}`
}

const TimeRangePicker = ({
  fromTime,
  toTime,
  onTimeChange,
  handleBlur,
  minFromTime,
  layout = 'inline',
  usdedInScheduling = false,
  disabled = false,
  hideDisabledOptions = false,
  fromInvalid = false,
}) => {
  const handleFromTimeChange = (newFromTime) => {
    let newToTime = toTime ? (toTime <= newFromTime ? '' : toTime) : ''

    if (newFromTime === '23:45') {
      newToTime = '23:45'
    }
    onTimeChange(newFromTime, newToTime)
    if (handleBlur) handleBlur()
  }

  const handleToTimeChange = (newToTime) => {
    if (
      newToTime > fromTime ||
      (fromTime === '23:45' && newToTime === '23:45')
    ) {
      onTimeChange(fromTime, newToTime)
      if (handleBlur) handleBlur()
    }
  }

  const fromOptions = useMemo(
    () =>
      TIME_STEPS.filter(
        (timeString) => !isFromTimeDisabled(timeString, minFromTime),
      ).map((timeString) => ({
        value: timeString,
        label: usdedInScheduling ? formatTimeLabel(timeString) : timeString,
      })),
    [minFromTime, usdedInScheduling],
  )

  const toOptions = useMemo(
    () =>
      TIME_STEPS.filter(
        (timeString) => !isToTimeDisabled(timeString, fromTime),
      ).map((timeString) => ({
        value: timeString,
        label: usdedInScheduling ? formatTimeLabel(timeString) : timeString,
      })),
    [fromTime, usdedInScheduling],
  )

  const renderTimeOptions = (minHour, isFromTime) => {
    const timeOptions = [
      <option key={isFromTime ? 'from' : 'to'} value=''>
        {isFromTime ? 'From' : 'To'}
      </option>,
    ]

    TIME_STEPS.forEach((timeString) => {
      const optionDisabled = isFromTime
        ? isFromTimeDisabled(timeString, minHour)
        : isToTimeDisabled(timeString, minHour)

      if (hideDisabledOptions && optionDisabled) {
        return
      }

      timeOptions.push(
        <option key={timeString} value={timeString} disabled={optionDisabled}>
          {usdedInScheduling ? formatTimeLabel(timeString) : timeString}
        </option>,
      )
    })
    return timeOptions
  }

  if (layout === 'split') {
    return (
      <>
        <ExperienceSelect
          options={fromOptions}
          value={fromTime || ''}
          placeholder='From'
          isInvalid={fromInvalid}
          isSearchable
          disabled={disabled}
          menuPlacement='auto'
          maxMenuHeight={260}
          minMenuHeight={240}
          onChange={(event) => handleFromTimeChange(event.target.value)}
          onBlur={handleBlur}
        />
        <ExperienceSelect
          options={toOptions}
          value={toTime || ''}
          placeholder='To'
          isSearchable
          disabled={disabled}
          menuPlacement='auto'
          maxMenuHeight={260}
          minMenuHeight={240}
          onChange={(event) => handleToTimeChange(event.target.value)}
          onBlur={handleBlur}
        />
      </>
    )
  }

  return (
    <div className='d-flex'>
      <select
        className={`selection-field ${usdedInScheduling ? '' : 'mr-3 mr-md-5'}`}
        value={fromTime || ''}
        disabled={disabled}
        onChange={(event) => handleFromTimeChange(event.target.value)}
        aria-label='From time'>
        {renderTimeOptions(minFromTime, true)}
      </select>
      {usdedInScheduling && <span className='mx-4 my-auto'>-</span>}
      <select
        className='selection-field'
        value={toTime || ''}
        disabled={disabled}
        onChange={(event) => handleToTimeChange(event.target.value)}
        aria-label='To time'>
        {renderTimeOptions(fromTime, false)}
      </select>
    </div>
  )
}

TimeRangePicker.propTypes = {
  fromTime: PropTypes.string,
  toTime: PropTypes.string,
  onTimeChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func,
  minFromTime: PropTypes.string,
  layout: PropTypes.oneOf(['inline', 'split']),
  usdedInScheduling: PropTypes.bool,
  disabled: PropTypes.bool,
  hideDisabledOptions: PropTypes.bool,
  fromInvalid: PropTypes.bool,
}

export default TimeRangePicker
